/*
 * The inbox's first reads, asked as soon as its address is known (frontend performance plan, W6,
 * "Reading the list alongside access"; Dawid, 2026-10-19, "Build it"). The page draws only once
 * access says the workspace has an inbox, so every read it makes started one round trip late. These
 * are the reads its first draw makes, under the same keys, so the page finds them asked or answered.
 *
 * Only for a workspace this browser last saw with its inbox on (`useRememberInbox`): a workspace
 * whose inbox is off is asked nothing the API would refuse, and a first visit waits for access as
 * before. The list's question depends on the order and mode the person chose, kept in this browser
 * under their id, which the session answers at the same time as access. So it is asked for the
 * person this browser's inbox last drew (`rememberedPerson`), with the defaults when none. If the
 * session names someone else, or the inbox was switched off since, a read is wasted, never wrong:
 * the page asks its own question under its own key. On first draw the search and filters are empty
 * and the page is the first. A saved view waits for its query, as the page does, so its list is not
 * asked.
 */
import type { QueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { z } from "zod";
import { PHONE_MAX_WIDTH } from "../../src/pane-layout.ts";
import { countsQuery, listQuery, needsReplyQuery, viewsQuery } from "../queries.ts";
import { mailChoiceFrom, ownerFrom, viewKey } from "../shell-nav.ts";
import { rememberedPerson, storedMode, storedSort } from "./list-prefs.ts";
import {
  highlightsQuery,
  NO_FILTERS,
  nextPlace,
  shortlistQuery,
  viewListQuery,
} from "./use-inbox.ts";

const inboxesKey = "leadcue-reply-inbox-workspaces";

/** The workspaces this browser last saw with their inbox on; none when storage is unavailable. */
function inboxesSeen(): string[] {
  try {
    const parsed = z
      .array(z.string())
      .safeParse(JSON.parse(localStorage.getItem(inboxesKey) ?? "[]"));
    return parsed.success ? parsed.data : [];
  } catch {
    return [];
  }
}

/** Keeps whether `workspaceId`'s inbox is on, as its access answer said, for the next visit. */
export function useRememberInbox(workspaceId: string | undefined, inbox: boolean | undefined) {
  useEffect(() => {
    if (!workspaceId || inbox === undefined) return;
    const seen = inboxesSeen();
    if (seen.includes(workspaceId) === inbox) return;
    const next = inbox ? [...seen, workspaceId] : seen.filter((id) => id !== workspaceId);
    try {
      localStorage.setItem(inboxesKey, JSON.stringify(next));
    } catch {
      // Private mode or blocked storage: the next visit waits for access, as a first one does.
    }
  }, [workspaceId, inbox]);
}

export function prefetchInboxFirstReads(
  client: QueryClient,
  workspaceId: string,
  search: string,
): void {
  if (!inboxesSeen().includes(workspaceId)) return;
  void client.prefetchQuery(countsQuery(workspaceId));
  void client.prefetchQuery(viewsQuery(workspaceId));
  void client.prefetchQuery(needsReplyQuery(workspaceId));
  const params = new URLSearchParams(search);
  // The empty reading pane's shortlist, drawn on a wider screen while nothing is open.
  const open = params.has("c") || params.has("plan");
  const owner = ownerFrom(search);
  // Who answers next reads from Meeting requests while any wait, else Needs reply (LEA-494): the
  // counts say which, so the shortlist and the strip are asked once they have, as the page asks them.
  const place = client.fetchQuery(countsQuery(workspaceId, owner)).then(
    (counts) => nextPlace(counts, false) ?? "needs_reply",
    () => "needs_reply" as const,
  );
  if (!open && window.innerWidth > PHONE_MAX_WIDTH)
    void place.then((from) => client.prefetchQuery(shortlistQuery(workspaceId, owner, from)));
  const choice = mailChoiceFrom(search);
  if (choice.kind === "view") return;
  const person = rememberedPerson();
  const sort =
    (person === null ? null : storedSort(person, viewKey(workspaceId, choice))) ?? "newest";
  const query = viewListQuery({ choice, owner, q: "", filters: NO_FILTERS, offset: 0, sort }, []);
  void client.prefetchQuery(listQuery(workspaceId, query, 0));
  if (person === null || storedMode(person) !== "highlights") return;
  void place.then((from) => {
    const strip = highlightsQuery(query, 0, from);
    return strip ? client.prefetchQuery(listQuery(workspaceId, strip)) : undefined;
  });
}
